import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /**
   * @type {boolean} Whether something on the page is driving this gallery's pictures.
   *
   * Set by that driver rather than by a setting: which gallery is being pointed at another
   * product is not a merchant's choice, and it is what tells a section render to leave this
   * one's slides alone.
   */
  reactive = false;

  /** @type {any[]} The products whose pictures are on screen, in the order they are worn. */
  #products = [];

  /**
   * The products the gallery shows.
   *
   * Given rather than fetched: several blocks want the same product, and the component that
   * knows which ones are in play — the builder — asks for them once and hands them round.
   *
   * More than one is a matching set. It opens on the first picture of each, side by side, and
   * carries the rest alternating, so a shopper sees both pairs before either one's detail.
   *
   * @param {any[]} value
   */
  set products(value) {
    const products = (Array.isArray(value) ? value : []).filter(Boolean);

    // The same products again: the slides on screen are already theirs, and rebuilding them
    // would take the picture away and put it back to arrive where it started.
    const same =
      products.length === this.#products.length &&
      products.every((product, index) => product.id === this.#products[index]?.id);
    if (products.length && same) return;

    this.#products = products;

    this.#renderMedia(products.map((product) => readImages(product)));
  }

  get products() {
    return this.#products;
  }

  /** @param {any} value - One product, for a gallery showing a single one. */
  set product(value) {
    this.products = value ? [value] : [];
  }

  get product() {
    return this.#products[0] ?? null;
  }

  /**
   * Show a product's pictures in place of the ones the page rendered.
   *
   * Only a gallery built to swap can do this: its images are `reactive-image`, which can be
   * handed a new url. Any other renders what its own page asked for, so it is left alone.
   *
   * @param {{ src: string, alt: string | null, aspect_ratio?: number }[][]} lists - One list of
   *   pictures per product.
   */
  #renderMedia(lists) {
    if (!this.querySelector('template[each]')) return;

    // Zoom shows one picture at a time, so it takes them alternating and whole. The slides
    // open on a pair, which is the same list with its first two shown together.
    const zoomed = alternate(lists);
    if (!zoomed.length) return;

    const images = lists.length > 1 ? [{ ...zoomed[0], beside: zoomed[1] }, ...zoomed.slice(2)] : zoomed;

    // Measured, not described. The `sizes` Liquid writes is the width the gallery has on a
    // product page — the viewport less a sidebar — and a gallery sitting in a builder's step is
    // a fraction of that, so the browser fetches a picture around twice the size it draws.
    // Taken before the clearing below, which leaves nothing on the page to measure.
    //
    // A gallery filled while its step is still hidden has no width to give, and a `sizes` of
    // nothing is read as nothing — the browser takes the smallest picture it has. So the
    // attribute Liquid wrote stands in: too generous for this column, but generous is the
    // direction that stays sharp.
    const slideWidth = Math.round(
      this.querySelector('slideshow-slide')?.getBoundingClientRect().width ||
        this.getBoundingClientRect().width ||
        0
    );
    const authoredSizes = this.querySelector('template[each]')?.content.querySelector('img')?.getAttribute('sizes') ?? '';

    // What the page rendered describes the product it was rendered for. `repeat()` clears
    // only clones of its own, so the server's are cleared here — once, since after that
    // every item in these lists is a clone.
    for (const template of this.querySelectorAll('template[each]')) {
      // Snapshotted: `children` is live, and removing from it as it is walked skips the next.
      for (const item of [...(template.parentElement?.children ?? [])]) {
        if (item !== template && !item.hasAttribute('data-bound-clone')) item.remove();
      }
    }

    // Named by the dialog on the page, since only the markup knows which block it belongs to.
    const dialogId = this.querySelector('zoom-dialog')?.id ?? '';

    /**
     * @param {any} image
     * @param {number} index - Where the picture sits in the list being built.
     * @param {number} count - How long that list is.
     * @param {number} zoomIndex - The same picture's place in the zoom list, which the slides
     *   address rather than their own, since a paired slide holds two of its entries.
     */
    const describe = (image, index, count, zoomIndex) => {
      // The shape the markup styles itself by: a shell has no media object to read it from.
      const ratio = image.aspect_ratio || (image.width && image.height ? image.width / image.height : 1);

      return {
        src: image.src,
        alt: image.alt ?? '',
        // The slide beside it, on the one slide that carries two.
        src2: image.beside?.src ?? '',
        alt2: image.beside?.alt ?? '',
        solo: !image.beside,
        paired: image.beside ? 'true' : 'false',
        // Halved where the slide holds two pictures.
        sizes: slideWidth ? `${image.beside ? Math.round(slideWidth / 2) : slideWidth}px` : authoredSizes,
        // The picture the gallery opens on is fetched at once. A swap happens while the step
        // it belongs to may still be hidden, and a lazy image told to load there is left
        // waiting for an intersection that showing the step does not produce.
        loading: index === 0 ? 'eager' : 'lazy',
        hidden: index === 0 ? 'false' : 'true',
        // The slideshow marks the open thumbnail, but it is already on the first slide when
        // these arrive, so it has no change to react to and never marks them.
        selected: index === 0 ? 'true' : 'false',
        // The whole attribute is bound, so what slideshow-slide writes for every slide has to
        // travel with it — a partial style would drop the rows the slide is laid out on.
        style:
          `--slideshow-timeline: --slide-${index}; --product-media-fit: cover; --ratio: ${ratio};` +
          ' --grid-template-rows: 50dvh 1fr; --grid-template-rows-desktop: 70dvh 1fr;',
        thumbStyle: `--aspect-ratio: ${ratio};`,
        // A thumbnail is a small crop of the same picture, and names the slide it selects.
        thumb: `${image.src}${image.src.includes('?') ? '&' : '?'}width=144`,
        select: `/select/${index}`,
        label: `${index + 1} of ${count}`,
        zoom: `#${dialogId}/open/${zoomIndex}`,
        // The dialog's own rail scrolls the zoomed gallery rather than the slideshow.
        zoomThumbClick: `/handleThumbnailClick/${index}`,
        zoomThumbEnter: `/handleThumbnailPointerEnter/${index}`,
      };
    };

    // A paired opening slide swallows the zoom list's second entry, so every slide after it
    // addresses the zoom entry one further along than its own position.
    const paired = images.length !== zoomed.length;
    const slides = images.map((image, index) =>
      describe(image, index, images.length, paired && index ? index + 1 : index)
    );
    const zoom = zoomed.map((image, index) => describe(image, index, zoomed.length, index));

    // The page's rail follows the slides, the dialog's follows the zoom list.
    this.render({ slides, thumbs: slides, zoomed: zoom, zoomThumbs: zoom }, this);

    this.slideshow?.select(0, undefined, { animate: false });
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * @param {any} product - A product as the Ajax Product API describes one.
 * @returns {any[]} Only what an `<img>` can show: `.js` reports videos and 3D models in the
 *   same list, and the markup this binds has no player to give them.
 */
function readImages(product) {
  return (product?.media ?? []).filter((item) => item.media_type === 'image');
}

/**
 * Interleave several products' pictures, so neither pair's detail shots come before the
 * other's. One product is simply its own pictures.
 *
 * @param {any[][]} lists - One list of pictures per product.
 * @returns {any[]}
 */
function alternate(lists) {
  const filled = lists.filter((list) => list.length);
  if (filled.length < 2) return filled[0] ?? [];

  const longest = Math.max(...filled.map((list) => list.length));
  const out = [];

  for (let index = 0; index < longest; index++) {
    for (const list of filled) if (list[index]) out.push(list[index]);
  }

  return out;
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
